import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import { UserCheck, Plus, CheckCircle, XCircle, Clock, UserPlus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';

const STATUS_CFG = {
    pending: { label: 'Menunggu', cls: 'badge-pending', icon: Clock },
    approved: { label: 'Disetujui', cls: 'badge-approved', icon: CheckCircle },
    rejected: { label: 'Ditolak', cls: 'badge-rejected', icon: XCircle }
};

const emptyForm = {
    guest_name: '', guest_nik: '', guest_address: '', origin: '',
    host_name: '', arrival_date: '', departure_date: '',
    stay_overnight: false, purpose: ''
};

export default function GuestReportPage() {
    const authCtx = useAuth();
    const { t } = useLang();
    const isLoggedIn = authCtx?.user;
    const canApprove = authCtx?.hasRole?.('sie_keamanan', 'ketua_rt', 'wakil', 'sekretaris');
    const [guests, setGuests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(!isLoggedIn);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [familyHeads, setFamilyHeads] = useState([]);
    const [hostSearch, setHostSearch] = useState('');
    const [showHostPicker, setShowHostPicker] = useState(false);

    const load = async () => {
        if (!isLoggedIn) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API}/guests`, { withCredentials: true });
            setGuests(res.data);
        } catch {}
        setLoading(false);
    };

    const loadFamilyHeads = async (q = '') => {
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/citizens?q=${q}&limit=30`);
            setFamilyHeads(res.data.data || []);
        } catch {}
    };

    useEffect(() => { load(); }, [isLoggedIn]);
    useEffect(() => { loadFamilyHeads(hostSearch); }, [hostSearch]);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!form.guest_name || !form.guest_nik || !form.host_name || !form.arrival_date || !form.purpose) {
            toast.error('Isi semua field wajib (termasuk NIK tamu)'); return;
        }
        setSaving(true);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/guests`, form);
            toast.success('Laporan tamu berhasil dikirim!');
            if (!isLoggedIn) { setSubmitted(true); }
            else { setShowForm(false); setForm(emptyForm); load(); }
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal mengirim laporan'); }
        setSaving(false);
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.put(`${API}/guests/${id}/status`, { status }, { withCredentials: true });
            toast.success(status === 'approved' ? 'Laporan disetujui' : 'Laporan ditolak');
            load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Gagal'); }
    };

    const GuestForm = () => (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label>Nama Tamu *</Label>
                    <Input value={form.guest_name} onChange={e => setForm(f => ({ ...f, guest_name: e.target.value }))}
                        placeholder="Nama lengkap tamu" data-testid="guest-name-input" />
                </div>
                <div className="space-y-1.5">
                    <Label>NIK Tamu * <span className="text-red-500">wajib</span></Label>
                    <Input value={form.guest_nik} onChange={e => setForm(f => ({ ...f, guest_nik: e.target.value }))}
                        placeholder="16 digit NIK" maxLength={16} data-testid="guest-nik-input" />
                </div>
            </div>
            <div className="space-y-1.5">
                <Label>Alamat Asal Tamu *</Label>
                <Input value={form.guest_address} onChange={e => setForm(f => ({ ...f, guest_address: e.target.value }))}
                    placeholder="Alamat lengkap asal tamu" data-testid="guest-address-input" />
            </div>
            <div className="space-y-1.5">
                <Label>Asal Daerah / Kota</Label>
                <Input value={form.origin} onChange={e => setForm(f => ({ ...f, origin: e.target.value }))}
                    placeholder="Kota/Kabupaten asal" data-testid="guest-origin-input" />
            </div>
            {/* Host selector */}
            <div className="space-y-1.5">
                <Label>Nama Tuan Rumah (Kepala Keluarga) *</Label>
                <div className="flex gap-2">
                    <Input value={form.host_name} onChange={e => setForm(f => ({ ...f, host_name: e.target.value }))}
                        placeholder="Ketik nama atau pilih dari daftar" data-testid="host-name-input" />
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowHostPicker(true)}
                        className="flex-shrink-0 gap-1.5 text-xs h-10 px-3" data-testid="pick-host-btn">
                        <Search className="w-3.5 h-3.5" /> Pilih
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                    <Label>Tanggal Tiba *</Label>
                    <Input type="date" value={form.arrival_date} onChange={e => setForm(f => ({ ...f, arrival_date: e.target.value }))}
                        data-testid="arrival-date-input" />
                </div>
                <div className="space-y-1.5">
                    <Label>Tanggal Pergi (opsional)</Label>
                    <Input type="date" value={form.departure_date} onChange={e => setForm(f => ({ ...f, departure_date: e.target.value }))} />
                </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <Switch checked={form.stay_overnight} onCheckedChange={v => setForm(f => ({ ...f, stay_overnight: v }))}
                    data-testid="stay-overnight-toggle" />
                <div>
                    <Label className="font-semibold">Menginap</Label>
                    <p className="text-xs text-slate-500">Aktifkan jika tamu akan menginap di rumah</p>
                </div>
            </div>
            <div className="space-y-1.5">
                <Label>Tujuan / Keperluan *</Label>
                <Textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))}
                    rows={2} placeholder="Tujuan kedatangan tamu" data-testid="purpose-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-primary hover:bg-primary/90" data-testid="submit-guest-btn">
                {saving ? 'Mengirim...' : 'Kirim Laporan'}
            </Button>
        </form>
    );

    // Public form
    if (!isLoggedIn) {
        if (submitted) {
            return (
                <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl border border-slate-200 p-8 max-w-md w-full text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <h2 className="text-2xl font-extrabold text-slate-800 mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            Laporan Terkirim!
                        </h2>
                        <p className="text-slate-500 mb-6">Laporan kedatangan tamu telah diterima dan akan segera diproses oleh petugas RT.</p>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => window.history.back()} className="flex-1">Kembali</Button>
                            <Button onClick={() => { setSubmitted(false); setForm(emptyForm); }} className="flex-1 bg-primary hover:bg-primary/90">
                                Lapor Tamu Lain
                            </Button>
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-md w-full">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                            <UserCheck className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="font-extrabold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                Wajib Lapor Tamu
                            </h1>
                            <p className="text-xs text-slate-500">Form laporan kedatangan tamu RT</p>
                        </div>
                    </div>
                    <GuestForm />
                    <div className="mt-4 text-center">
                        <a href="/" className="text-xs text-slate-400 hover:text-slate-600">← Kembali ke halaman utama</a>
                    </div>
                </div>
                {/* Host picker */}
                <Dialog open={showHostPicker} onOpenChange={setShowHostPicker}>
                    <DialogContent className="max-w-sm">
                        <DialogHeader><DialogTitle>Pilih Tuan Rumah (KK)</DialogTitle></DialogHeader>
                        <Input placeholder="Cari nama kepala keluarga..." value={hostSearch}
                            onChange={e => setHostSearch(e.target.value)} className="mb-2" />
                        <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
                            {familyHeads.filter(c => c.head_of_family?.toLowerCase().includes(hostSearch.toLowerCase())).map(c => (
                                <button key={c.id} onClick={() => { setForm(f => ({ ...f, host_name: c.head_of_family })); setShowHostPicker(false); }}
                                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors text-sm">
                                    <span className="font-medium text-slate-800">{c.head_of_family}</span>
                                    <span className="text-slate-400 text-xs ml-2">{c.address}</span>
                                </button>
                            ))}
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    }

    const pending = guests.filter(g => g.status === 'pending');
    const processed = guests.filter(g => g.status !== 'pending');

    return (
        <div className="space-y-5" data-testid="guest-report-page">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h1 className="page-header text-2xl">{t.guestTitle}</h1>
                    <p className="text-sm text-slate-500 mt-1">{pending.length} laporan menunggu persetujuan</p>
                </div>
                <Button onClick={() => setShowForm(true)} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-guest-btn">
                    <UserPlus className="w-4 h-4" /> Lapor Tamu Baru
                </Button>
            </div>

            <Tabs defaultValue="pending">
                <TabsList className="bg-slate-100">
                    <TabsTrigger value="pending">Menunggu ({pending.length})</TabsTrigger>
                    <TabsTrigger value="processed">Diproses ({processed.length})</TabsTrigger>
                </TabsList>
                {['pending', 'processed'].map(tab => (
                    <TabsContent key={tab} value={tab} className="mt-4">
                        {loading ? (
                            <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>
                        ) : (tab === 'pending' ? pending : processed).length === 0 ? (
                            <div className="bg-white rounded-xl border border-slate-200 py-12 text-center">
                                <UserCheck className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                                <p className="text-slate-400">Tidak ada laporan</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(tab === 'pending' ? pending : processed).map(g => {
                                    const cfg = STATUS_CFG[g.status] || STATUS_CFG.pending;
                                    return (
                                        <div key={g.id} className="bg-white rounded-xl border border-slate-200 p-5">
                                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                                        <p className="font-bold text-slate-800">{g.guest_name}</p>
                                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                                                        {g.stay_overnight && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium">Menginap</span>}
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-sm text-slate-600 mt-2">
                                                        <p><span className="text-slate-400 text-xs">NIK:</span> {g.guest_nik || '—'}</p>
                                                        <p><span className="text-slate-400 text-xs">Asal:</span> {g.origin || g.guest_address}</p>
                                                        <p><span className="text-slate-400 text-xs">Tuan rumah:</span> {g.host_name}</p>
                                                        <p><span className="text-slate-400 text-xs">Tiba:</span> {g.arrival_date}{g.departure_date && ` — ${g.departure_date}`}</p>
                                                        <p className="md:col-span-2"><span className="text-slate-400 text-xs">Tujuan:</span> {g.purpose}</p>
                                                    </div>
                                                    <p className="text-xs text-slate-400 mt-2">Dilaporkan: {new Date(g.created_at).toLocaleString('id-ID')}</p>
                                                </div>
                                                {canApprove && g.status === 'pending' && (
                                                    <div className="flex gap-2 flex-shrink-0">
                                                        <Button size="sm" onClick={() => updateStatus(g.id, 'approved')} className="bg-green-600 hover:bg-green-700 gap-1.5 h-8 text-xs"
                                                            data-testid={`approve-guest-${g.id}`}>
                                                            <CheckCircle className="w-3.5 h-3.5" /> Setujui
                                                        </Button>
                                                        <Button size="sm" variant="outline" onClick={() => updateStatus(g.id, 'rejected')} className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5 h-8 text-xs"
                                                            data-testid={`reject-guest-${g.id}`}>
                                                            <XCircle className="w-3.5 h-3.5" /> Tolak
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>
                ))}
            </Tabs>

            <Dialog open={showForm} onOpenChange={setShowForm}>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Lapor Kedatangan Tamu Baru</DialogTitle></DialogHeader>
                    <GuestForm />
                </DialogContent>
            </Dialog>

            {/* Host picker dialog */}
            <Dialog open={showHostPicker} onOpenChange={setShowHostPicker}>
                <DialogContent className="max-w-sm">
                    <DialogHeader><DialogTitle>Pilih Tuan Rumah (KK)</DialogTitle></DialogHeader>
                    <Input placeholder="Cari nama kepala keluarga..." value={hostSearch}
                        onChange={e => setHostSearch(e.target.value)} className="mb-2" />
                    <div className="max-h-52 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-xl">
                        {familyHeads.filter(c => c.head_of_family?.toLowerCase().includes(hostSearch.toLowerCase())).map(c => (
                            <button key={c.id} onClick={() => { setForm(f => ({ ...f, host_name: c.head_of_family })); setShowHostPicker(false); }}
                                className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors text-sm">
                                <span className="font-medium text-slate-800">{c.head_of_family}</span>
                                <span className="text-slate-400 text-xs ml-2">{c.address}</span>
                            </button>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
